import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with interceptor
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('rhe_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('rhe_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => apiClient.post('/auth/login', { email, password }),
  me: () => apiClient.get('/auth/me'),
  resetPassword: (email) => apiClient.post('/auth/reset-password', { email }),
  requestPasswordReset: (email) => apiClient.post('/auth/request-password-reset', { email }),
  completePasswordReset: (token, newPassword) => apiClient.post('/auth/complete-password-reset', { token, new_password: newPassword }),
  changePassword: (currentPassword, newPassword) => apiClient.post('/auth/change-password', null, {
    params: { current_password: currentPassword, new_password: newPassword }
  }),
};

// Users API
export const usersAPI = {
  getAll: () => apiClient.get('/users'),
  getSubcontractors: () => apiClient.get('/users/subcontractors'),
  getById: (id) => apiClient.get(`/users/${id}`),
  create: (data) => apiClient.post('/users', data),
  update: (id, data) => apiClient.put(`/users/${id}`, data),
  delete: (id) => apiClient.delete(`/users/${id}`),
  getAvailableJobs: (userId) => apiClient.get(`/users/${userId}/available-jobs`),
  resetPassword: (userId, newPassword) => apiClient.post(`/users/${userId}/reset-password`, null, {
    params: { new_password: newPassword }
  }),
  // Document attachments
  getDocuments: (userId) => apiClient.get(`/users/${userId}/documents`),
  uploadDocument: (userId, file, description = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description);
    return apiClient.post(`/users/${userId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadDocument: (userId, documentId) => apiClient.get(`/users/${userId}/documents/${documentId}`, {
    responseType: 'blob'
  }),
  deleteDocument: (userId, documentId) => apiClient.delete(`/users/${userId}/documents/${documentId}`),
};

// Clients API
export const clientsAPI = {
  getAll: () => apiClient.get('/clients'),
  getById: (id) => apiClient.get(`/clients/${id}`),
  create: (data) => apiClient.post('/clients', data),
  update: (id, data) => apiClient.put(`/clients/${id}`, data),
  delete: (id) => apiClient.delete(`/clients/${id}`),
  // Document attachments
  getDocuments: (clientId) => apiClient.get(`/clients/${clientId}/documents`),
  uploadDocument: (clientId, file, description = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description);
    return apiClient.post(`/clients/${clientId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadDocument: (clientId, documentId) => apiClient.get(`/clients/${clientId}/documents/${documentId}`, {
    responseType: 'blob'
  }),
  deleteDocument: (clientId, documentId) => apiClient.delete(`/clients/${clientId}/documents/${documentId}`),
};

// Jobs API
export const jobsAPI = {
  getAll: (params) => apiClient.get('/jobs', { params }),
  getActive: () => apiClient.get('/jobs', { params: { active_only: true } }),
  getById: (id) => apiClient.get(`/jobs/${id}`),
  getByClient: (clientId) => apiClient.get('/jobs', { params: { client_id: clientId } }),
  create: (data) => apiClient.post('/jobs', data),
  update: (id, data) => apiClient.put(`/jobs/${id}`, data),
  delete: (id) => apiClient.delete(`/jobs/${id}`),
  // Document attachments
  getDocuments: (projectId) => apiClient.get(`/projects/${projectId}/documents`),
  uploadDocument: (projectId, file, description = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description);
    return apiClient.post(`/projects/${projectId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadDocument: (projectId, documentId) => apiClient.get(`/projects/${projectId}/documents/${documentId}`, {
    responseType: 'blob'
  }),
  deleteDocument: (projectId, documentId) => apiClient.delete(`/projects/${projectId}/documents/${documentId}`),
};

// Resources API
export const resourcesAPI = {
  getAll: () => apiClient.get('/resources'),
  getActive: () => apiClient.get('/resources', { params: { active_only: true } }),
  getById: (id) => apiClient.get(`/resources/${id}`),
  getByUser: (userId) => apiClient.get(`/resources/user/${userId}`),
  create: (data) => apiClient.post('/resources', data),
  update: (id, data) => apiClient.put(`/resources/${id}`, data),
  delete: (id) => apiClient.delete(`/resources/${id}`),
  // Pay rates
  getPayRates: (resourceId) => apiClient.get(`/resources/${resourceId}/pay-rates`),
  getPayRateForJob: (resourceId, jobId) => apiClient.get(`/resources/${resourceId}/pay-rates/${jobId || 'default'}`),
  upsertPayRate: (resourceId, data) => apiClient.put(`/resources/${resourceId}/pay-rates`, data),
  deletePayRate: (payRateId) => apiClient.delete(`/pay-rates/${payRateId}`),
};

// Timesheets API
export const timesheetsAPI = {
  getAll: (status) => apiClient.get('/timesheets', { params: status ? { status } : {} }),
  getMy: () => apiClient.get('/timesheets/my'),
  getMyCurrent: () => apiClient.get('/timesheets/my/current'),
  getCurrentPeriod: () => apiClient.get('/timesheets/current-period'),
  getPending: () => apiClient.get('/timesheets/pending'),
  getApprovedUninvoiced: () => apiClient.get('/timesheets/approved-uninvoiced'),
  getById: (id) => apiClient.get(`/timesheets/${id}`),
  getApprovedByUser: (userId) => apiClient.get(`/timesheets/user/${userId}/approved`),
  estimatePay: (timesheetIds, customEntryIds = []) => apiClient.post('/timesheets/estimate-pay', { timesheet_ids: timesheetIds, custom_entry_ids: customEntryIds }),
  estimateBilling: (timesheetIds, customEntryIds = []) => apiClient.post('/timesheets/estimate-billing', { timesheet_ids: timesheetIds, custom_entry_ids: customEntryIds }),
  getPaymentStatus: (id) => apiClient.get(`/timesheets/${id}/payment-status`),
  create: (data) => apiClient.post('/timesheets', data),
  update: (id, data) => apiClient.put(`/timesheets/${id}`, data),
  submit: (id) => apiClient.post(`/timesheets/${id}/submit`),
  approve: (id) => apiClient.post(`/timesheets/${id}/approve`),
  batchApprove: (timesheetIds) => apiClient.post('/timesheets/batch-approve', { timesheet_ids: timesheetIds }),
  reject: (id, comment) => apiClient.post(`/timesheets/${id}/reject`, { comment }),
  delete: (id) => apiClient.delete(`/timesheets/${id}`),
  // Attachment methods
  getAttachments: (id) => apiClient.get(`/timesheets/${id}/attachments`),
  uploadAttachment: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/timesheets/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadAttachment: (timesheetId, attachmentId) => 
    apiClient.get(`/timesheets/${timesheetId}/attachments/${attachmentId}`, { responseType: 'blob' }),
  deleteAttachment: (timesheetId, attachmentId) => 
    apiClient.delete(`/timesheets/${timesheetId}/attachments/${attachmentId}`),
};

// Custom Timesheet Entries API (Admin only)
export const customEntriesAPI = {
  getAll: () => apiClient.get('/custom-entries'),
  getById: (id) => apiClient.get(`/custom-entries/${id}`),
  create: (data) => apiClient.post('/custom-entries', data),
  update: (id, data) => apiClient.put(`/custom-entries/${id}`, data),
  delete: (id) => apiClient.delete(`/custom-entries/${id}`),
  approve: (id) => apiClient.post(`/custom-entries/${id}/approve`),
  reject: (id, comment) => apiClient.post(`/custom-entries/${id}/reject`, null, { params: { comment } }),
  // Attachment methods
  getAttachments: (id) => apiClient.get(`/custom-entries/${id}/attachments`),
  uploadAttachment: (id, formData) => apiClient.post(`/custom-entries/${id}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadAttachment: (entryId, attachmentId) => apiClient.get(`/custom-entries/${entryId}/attachments/${attachmentId}`, {
    responseType: 'blob'
  }),
  deleteAttachment: (entryId, attachmentId) => apiClient.delete(`/custom-entries/${entryId}/attachments/${attachmentId}`),
};

// Client Invoices API
export const clientInvoicesAPI = {
  getAll: (clientId) => apiClient.get('/invoices/client', { params: clientId ? { client_id: clientId } : {} }),
  getOutstanding: () => apiClient.get('/invoices/client/outstanding'),
  getById: (id) => apiClient.get(`/invoices/client/${id}`),
  create: (data) => apiClient.post('/invoices/client', data),
  update: (id, data) => apiClient.put(`/invoices/client/${id}`, data),
  updateStatus: (id, status, paidDate) => apiClient.put(`/invoices/client/${id}/status`, { status, paid_date: paidDate }),
  delete: (id) => apiClient.delete(`/invoices/client/${id}`),
};

// Subcontractor Invoices API
export const subcontractorInvoicesAPI = {
  getAll: (userId) => apiClient.get('/invoices/subcontractor', { params: userId ? { user_id: userId } : {} }),
  getById: (id) => apiClient.get(`/invoices/subcontractor/${id}`),
  create: (data) => apiClient.post('/invoices/subcontractor', data),
  update: (id, data) => apiClient.put(`/invoices/subcontractor/${id}`, data),
  updateStatus: (id, status, paidDate) => apiClient.put(`/invoices/subcontractor/${id}/status`, { status, paid_date: paidDate }),
  delete: (id) => apiClient.delete(`/invoices/subcontractor/${id}`),
};

// Financial API
export const financialAPI = {
  getKPIs: () => apiClient.get('/dashboard/kpis'),
  getYetToBeCharged: (clientId, startDate, endDate) => apiClient.get('/dashboard/yet-to-be-charged', { 
    params: { client_id: clientId || undefined, start_date: startDate || undefined, end_date: endDate || undefined } 
  }),
  getYetToBePaid: (clientId, startDate, endDate) => apiClient.get('/dashboard/yet-to-be-paid', { 
    params: { client_id: clientId || undefined, start_date: startDate || undefined, end_date: endDate || undefined } 
  }),
  getSummary: (params) => apiClient.get('/financial/summary', { params }),
  getMonthly: (year, clientId, jobId) => apiClient.get('/financial/monthly', { params: { year, client_id: clientId, job_id: jobId } }),
  getByClient: (startDate, endDate) => apiClient.get('/financial/by-client', { params: { start_date: startDate, end_date: endDate } }),
  getByJob: (startDate, endDate) => apiClient.get('/financial/by-job', { params: { start_date: startDate, end_date: endDate } }),
};

// Xero API
export const xeroAPI = {
  getStatus: () => apiClient.get('/xero/status'),
  getAuthUrl: () => apiClient.get('/xero/auth-url'),
  disconnect: () => apiClient.post('/xero/disconnect'),
  syncClients: () => apiClient.post('/xero/sync-clients'),
  syncInvoices: () => apiClient.post('/xero/sync-invoices'),
  createInvoice: (invoiceId) => apiClient.post(`/xero/create-invoice/${invoiceId}`),
};

// Audit Logs API
export const auditAPI = {
  getLogs: (params) => apiClient.get('/audit-logs', { params }),
  getRecent: (limit) => apiClient.get('/audit-logs/recent', { params: { limit } }),
};

// Reports/PDF API
export const reportsAPI = {
  downloadTimesheetPDF: (timesheetId) => 
    apiClient.get(`/reports/timesheet/${timesheetId}/pdf`, { responseType: 'blob' }),
  downloadMonthlyReportPDF: (year, month, filterType, filterId, startDate, endDate) => 
    apiClient.get('/reports/monthly/pdf', { 
      params: { 
        year: startDate ? undefined : year, 
        month: startDate ? undefined : month, 
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        filter_type: filterType, 
        filter_id: filterId 
      },
      responseType: 'blob'
    }),
  downloadClientSummaryPDF: (year, month, clientId, projectId, startDate, endDate) =>
    apiClient.get('/reports/client-summary/pdf', {
      params: { 
        year: startDate ? undefined : year, 
        month: startDate ? undefined : month,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        client_id: clientId, 
        project_id: projectId || undefined 
      },
      responseType: 'blob'
    }),
  sendClientSummaryEmail: (year, month, clientId, emailData, projectId, startDate, endDate) =>
    apiClient.post('/reports/client-summary/send-email', emailData, {
      params: { 
        year: startDate ? undefined : year, 
        month: startDate ? undefined : month,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        client_id: clientId, 
        project_id: projectId || undefined 
      }
    }),
};

// Email Settings API
export const emailSettingsAPI = {
  get: () => apiClient.get('/settings/email'),
  update: (data) => apiClient.put('/settings/email', data),
  getTemplates: () => apiClient.get('/settings/email/templates'),
  createTemplate: (data) => apiClient.post('/settings/email/templates', data),
  updateTemplate: (id, data) => apiClient.put(`/settings/email/templates/${id}`, data),
  deleteTemplate: (id) => apiClient.delete(`/settings/email/templates/${id}`),
};

export default apiClient;
